import React, { useEffect, useContext, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { CardNotification, Loading, toast } from "@foris/avocado-suite";
import Layout from "@common/layout/Layout";
import { GroupsManagerSearchOrderByFields, OrderByDirection } from "@models/ISchema";
import splitGroupErrors from "../../utils/splitGroupErrors";
import { ContextEDH } from "@context/ContextEDH";
import { Context } from "../../context/GroupsManagerContext";
import { Types as DataTypes } from "../../context/data.reducer";
import { Types as EditionTypes } from "../../context/editions.reducer";
import { Types as NavigationTypes } from "../../context/navigation.reducer";
import { Types as FilterSelectorsTypes } from "../../context/filtersSelectors.reducer";
import {
  GroupsTableFiltersState,
  setTableFiltersOrderBy,
  setTableFiltersSearchBy,
  useGroupsTableFilters,
} from "../../context/useGroupsTableFilters";
import { FiltersReducerType } from "../../context/types";
import {
  AdaptedGroup,
  AdaptedValidationErrorReason,
  DEFAULT_PAGE_SIZE,
  GroupEdition,
} from "../../models";
import { useGetGroups } from "../../hooks/useGetGroups";
import useGroupsManagerCrud from "../../hooks/useGroupsManagerCrud";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useContextUrl from "@common/hooks/useContextUrl";
import GroupsManagerTable from "../../components/GroupsManagerTable/GroupsManagerTable";
import Filters from "../../components/Filters/Filters";
import BottomDetail from "../../components/BottomDetail/BottomDetail";
import { Header } from "@common/components";
import styles from "./groupsManager.module.scss";

const FORCED_WAITING_TIME = 4000; // ms

const GroupsManagerApp = () => {
  const { t } = useTranslation();
  const { origin, scenario, workspace } = useContextUrl();
  const { homeUrl } = useNavigationUrl();
  const { state: outerState } = useContext(ContextEDH);
  const { state, dispatch } = useContext(Context);
  const returnLocation = state?.navigation?.locationStack?.slice(-1)[0];
  const isRestoringList = !!state?.navigation?.forceFetching && !!returnLocation;
  const tableFilters = useGroupsTableFilters();
  const [loading, setLoading] = useState(false);
  const [internalPageSize, setInternalPageSize] = useState(
    isRestoringList ? (returnLocation.pageSize ?? DEFAULT_PAGE_SIZE) : DEFAULT_PAGE_SIZE,
  );
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const [{ response: groups, pageInfo: groupsPageInfo, isLoading }, getGroups] = useGetGroups();
  const [lastUpdateDateTime, setLastUpdateDateTime] = useState<string>(
    dayjs().format("DD/MM/YYYY HH:mm"),
  );
  const [avoidEditionsCleaningAfterRequest, setAvoidEditionsCleaningAfterRequest] = useState(false);
  const [, submitEditions] = useGroupsManagerCrud({ scenario, origin });
  const somethingWasRequested = (state?.data?.groupsPageInfo?.page ?? 0) > 0;
  const userAbilities = outerState?.base?.base?.user?.abilities ?? {};
  const [savedFilters, setSavedFilters] = useState<FiltersReducerType>(state?.filters);
  const [savedOrderBy, setSavedOrderBy] = useState<GroupsTableFiltersState["orderBy"]>(
    tableFilters.orderBy,
  );
  const [savedSearchBy, setSavedSearchBy] = useState<GroupsTableFiltersState["searchBy"]>(
    tableFilters.searchBy,
  );

  const isLoadingGroups = isLoadingInternal || isLoading;

  const handleRefresh = () => {
    setIsLoadingInternal(true);
    setAvoidEditionsCleaningAfterRequest(true);
    getGroups(
      savedFilters,
      { page: groupsPageInfo?.page ?? 1, size: internalPageSize },
      savedOrderBy,
      savedSearchBy,
    );
    setLastUpdateDateTime(dayjs().format("DD/MM/YYYY HH:mm"));
  };

  const requestGroups = ({
    page,
    pageSize = internalPageSize,
    avoidEditionsCleaning,
    orderBy,
    searchBy,
  }: {
    page: number;
    pageSize?: number;
    avoidEditionsCleaning: boolean;
    orderBy: GroupsTableFiltersState["orderBy"];
    searchBy: GroupsTableFiltersState["searchBy"];
  }) => {
    // save the filters and orderBy and searchBy to be able to restore them when the user clicks on the refresh button
    setSavedFilters(state?.filters);
    setSavedOrderBy(orderBy);
    setSavedSearchBy(searchBy);

    setIsLoadingInternal(true);
    setAvoidEditionsCleaningAfterRequest(avoidEditionsCleaning);
    getGroups(state?.filters, { page, size: pageSize }, orderBy, searchBy);
    pageSize && pageSize !== internalPageSize && setInternalPageSize(pageSize);
    setLastUpdateDateTime(dayjs().format("DD/MM/YYYY HH:mm"));
  };

  const onSave = async () => {
    setLoading(true);

    // exclude groups with hard errors
    const [hardErrorsByGroupId] = splitGroupErrors(state?.editions?.errorsByGroupId);
    const hardErrorGroupIds = new Set(Object.keys(hardErrorsByGroupId));
    const editionsToSubmit = Object.fromEntries(
      Object.entries(state?.editions?.byGroupId ?? {}).filter(
        ([groupId]) => !hardErrorGroupIds.has(groupId),
      ),
    ) as Record<AdaptedGroup["id"], GroupEdition>;

    await submitEditions(editionsToSubmit);

    setTimeout(() => {
      for (const [groupId, edition] of Object.entries(editionsToSubmit)) {
        if (edition?.capacity !== undefined) {
          dispatch({
            type: EditionTypes.AddPendingCapacity,
            payload: { groupId, capacity: edition?.capacity },
          });
        }
      }

      dispatch({ type: EditionTypes.Clean });

      requestGroups({
        page: groupsPageInfo?.page ?? 1,
        avoidEditionsCleaning: false,
        orderBy: tableFilters.orderBy,
        searchBy: tableFilters.searchBy,
      });

      toast.warning(t("groups-manager.notifications.change-request-sent"), {
        autoClose: 3000,
      });

      setLoading(false);
    }, FORCED_WAITING_TIME);
  };

  /**
   * Update the context's `groups` and `groupsById` after request
   */
  useEffect(() => {
    dispatch({ type: DataTypes.SetGroups, payload: groups ?? [] });
    dispatch({ type: DataTypes.SetGroupsById, payload: groups ?? [] });
    dispatch({ type: DataTypes.SetGroupsPageInfo, payload: groupsPageInfo });

    if (!avoidEditionsCleaningAfterRequest) {
      dispatch({ type: EditionTypes.Clean });
      setAvoidEditionsCleaningAfterRequest(false);
    }

    // if the group is editable or the reason why is not editable is different
    // that a pending change request, and it exists on the
    // `pendingCapacitiesByGroupId`, remove it.
    for (const group of groups ?? []) {
      if (
        group?.isEditable?.reason !== AdaptedValidationErrorReason?.PendingChangeRequest &&
        Object.prototype.hasOwnProperty.call(
          state?.editions?.pendingCapacitiesByGroupId ?? {},
          group?.id,
        )
      ) {
        dispatch({
          type: EditionTypes.RemovePendingCapacity,
          payload: group?.id,
        });
      }
    }

    setIsLoadingInternal(false);
  }, [groups, groupsPageInfo]);

  /**
   * Since the `forceFetching` is setted to `true` only when we're coming back from
   * the group's detail page, this value can be used to request the groups automatically
   * when we enter the main groups page. The request will not be triggered if the
   * location stack is emtpy.
   */
  useEffect(() => {
    if (isRestoringList) {
      requestGroups({
        page: returnLocation.page ?? 1,
        avoidEditionsCleaning: false,
        orderBy: tableFilters.orderBy,
        searchBy: tableFilters.searchBy,
      });
      dispatch({ type: NavigationTypes.PopFromLocationStack });
    }
    dispatch({ type: NavigationTypes.SetForceFetching, payload: false });
  }, [state?.navigation?.forceFetching]);

  /**
   * Set the group's CRN as the default order when the list mounts.
   */
  useEffect(() => {
    setTableFiltersOrderBy({
      header: "CRN",
      direction: OrderByDirection.Desc,
      field: GroupsManagerSearchOrderByFields.GroupCode,
    });
  }, []);

  return (
    <Layout contextSearch>
      {loading && <Loading />}

      <Header
        breadcrumbItems={[
          {
            path: homeUrl(workspace, scenario, origin),
            title: t("sidebar-menu.titles.home"),
          },
          {
            title: t("groups-manager.title"),
          },
        ]}
        title={t("groups-manager.title")}
      />

      <div className={styles.groupsManagerApp}>
        <Filters
          isRestoringList={isRestoringList}
          requestGroups={({ page, avoidEditionsCleaning, usedFilter }) => {
            if (usedFilter !== "KEY") {
              setTableFiltersSearchBy({ fields: [], text: "" });
            }

            dispatch({ type: FilterSelectorsTypes.SetUsedFilterToSearch, payload: usedFilter });
            requestGroups?.({
              page,
              avoidEditionsCleaning: !!avoidEditionsCleaning,
              orderBy: tableFilters.orderBy,
              searchBy: usedFilter === "KEY" ? tableFilters.searchBy : { fields: [], text: "" },
            });
          }}
        />

        {!!somethingWasRequested &&
          !groupsPageInfo?.total &&
          !isLoadingGroups &&
          !tableFilters.searchBy?.text && (
            <CardNotification
              className={styles.groupsManagerApp_noResults}
              state="info"
              title={t("groups-manager.groups-table.empty-results.title")}
              outlined
            >
              {t("groups-manager.groups-table.empty-results.description")}
            </CardNotification>
          )}

        {(isLoadingGroups ||
          (!!somethingWasRequested && !!groupsPageInfo?.total) ||
          (!!somethingWasRequested && !!tableFilters.searchBy?.text)) && (
          <>
            <GroupsManagerTable
              lastUpdateDateTime={lastUpdateDateTime}
              activeFilterType={state?.filtersSelectors?.usedFilterToSearch}
              requestGroups={({ page, avoidEditionsCleaning, pageSize, orderBy, searchBy }) =>
                requestGroups?.({
                  page,
                  pageSize,
                  avoidEditionsCleaning: !!avoidEditionsCleaning,
                  orderBy: orderBy ?? tableFilters.orderBy,
                  searchBy: searchBy ?? tableFilters.searchBy,
                })
              }
              pageInfo={groupsPageInfo}
              pageSize={internalPageSize}
              isLoading={isLoadingGroups}
              onRefresh={handleRefresh}
              canRefresh={!!savedFilters?.campus?.id || !!savedFilters?.subject?.id}
            />
            {!!userAbilities?.can_edit_groups_in_group_manager && (
              <BottomDetail onSave={onSave} isLoading={loading || isLoadingGroups} />
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default GroupsManagerApp;
